import { Component, ElementRef, OnInit, Input, ViewChild } from '@angular/core';
import { CfChartComponent } from '../chart/chart.component';
import { AreaChartModel } from '../../models/chart/area-chart.model';
import { AreaChartTemplates } from '../../templates/area-chart.template';
import { ChartStylingModel } from '../../models/chart/chart-styling.model';
import { TemplateService } from '../../services/template-service/template.service';
import { CfEventService } from '../../services/event-service/event.service';
/**
 * <p>CfAreaChart component is created based on <a href="https://github.com/swimlane/ngx-charts" target="_blank">Ngx-charts</a></b>
 * and <b>CfChartComponet</b></p>
 * <code><pre>
 * import {{'{'}} AreaChartModel {{'}'}} from 'cedrus-fusion'
 * import {{'{'}} ChartStylingModel {{'}'}} from 'cedrus-fusion'
 * <code><</code>cf-area-chart<code>></code><code><</code><code>/</code>cf-area-chart<code>></code>
 * </pre></code>
 * <code><pre>{{'{'}}
 * 	data: any[], 										// Data to show
 *  view: number[]                  // Dimensions of chart [width, height] by px
 * 	gradient: boolean, 							// Fill elements with a gradient instead of a solid color. Default: false.
 * 	showLegend: boolean, 						// If to show the legend. Default: true.
 * 	colorScheme: ChartsColorScheme, // Array with string css color values. Default: ['#5AA454', '#A10A28', '#C7B42C', '#AAAAAA'].
 * 	showXAxis: boolean, 						// If to show the x axis. Default: true.
 * 	showYAxis: boolean, 						// If to show the y axis. Default: true.
 * 	showXAxisLabel: boolean, 				// If to show the x axis label. Default: true.
 * 	showYAxisLabel: boolean, 				// If to show the y axis label. Default: true.
 * 	xAxisLabel: string, 						// The x axis label
 * 	yAxisLabel: string, 						// The y axis label
 * 	autoScale: boolean,							// Set the minimum value of the y axis to the minimum value in the data, instead of 0. Default: false.
 * 	stacked: boolean,								// If area chart is show in stacked mode. Default: false.
 * 	legendPosition: string,					// Legend position. Can be: top, right, bottom, left. Default: right.
 * 	chartAlignment: string,					// Alignment of chart. Can be: start, center, end. Default: start.
 * {{'}'}}</pre></code>
 * <p>Example of data object:</p>
 * <code><pre>data = [
 *  {{'{'}}
 *    "name": "Germany",
 *    "series": [
 *    	{{'{'}} "name": "2010", "value": 7300000 {{'}'}},
 *    	{{'{'}} "name": "2011", "value": 8940000 {{'}'}}
 *    ]
 *  {{'}'}},
 *  {{'{'}}
 *    "name": "USA",
 *    "series": [
 *    	{{'{'}} "name": "2010", "value": 7870000 {{'}'}},
 *    	{{'{'}} "name": "2011", "value": 8270000 {{'}'}}
 *    ]
 *  {{'}'}},
 *  {{'{'}}
 *    "name": "France",
 *    "series": [
 *    	{{'{'}} "name": "2010", "value": 5000002 {{'}'}},
 *    	{{'{'}} "name": "2011", "value": 5800000 {{'}'}}
 *     ]
 *   {{'}'}}
 * ]</pre></code>
 * <p>Example of chart options:</p>
 * <code><pre>myChartModel = new AreaChartModel({{'{'}}
 * 	data: this.data,
 * 	stacked: true
 * {{'}'}});</pre></code>
 * <p>Styling</p>
 * <code><pre>{{'{'}}
 * 	barsContainerWidth: string, 							// It is css value for chart bars container width. Default: 100%.
 * 	barsContainerHeight: string', 						// It is css value for chart bars container height. Default: 200px.
 * 	container: StylingObject, 	// Styling of chart container
 * {{'}'}}</pre></code>
 * <p>Example of styling object:</p>
 * <code><pre>chartStylingModel = new ChartStylingModel({{'{'}}
 * 	barsContainerWidth: '100%',
 * 	barsContainerHeight: '200px',
 * 	container: {{'{'}}
 * 		class: 'my-chart-class'
 * 	{{'}'}}
 * {{'}'}});</pre></code>
 */
export class CfAreaChartComponent extends CfChartComponent implements OnInit {
/**
 * @hidden
 */
cfChartHost;
/**
 * @hidden
 */
cfNgxChart;
/**
 * <p>Defines if the chart is stacked. Default: false.</p>
 * @param {?} value
 * @return {?}
 */
set stacked(value: boolean) { this.properties.stacked = value; }
/**
 * <p>Set the minimum value of the y axis to the minimum value in the data, instead of 0. Default: false.</p>
 * @param {?} value
 * @return {?}
 */
set autoScale(value: boolean) { this.properties.autoScale = value; }
/**
 * @hidden
 * @param {?} elementRef
 * @param {?} templateService
 * @param {?} eventService
 */
constructor(public elementRef: ElementRef,
                /**@hidden */ templateService: TemplateService,
public eventService: CfEventService) {
    super(elementRef, templateService, eventService);
    this.properties = new AreaChartModel();
  }
/**
 * @hidden
 * @return {?}
 */
ngOnInit(): void {
    super.ngOnInit();
    this.getMyTemplate('area-chart', AreaChartTemplates).then(() => {
      this.mergeProperties(this.activeTemplate.property, this.properties, 'area-chart').then( sampler => {
        if (this.properties.colorScheme.domain.length === 0) {
          this.properties.colorScheme = this.colorSchemes.find(s => s.name === this.properties.colorSchemeName);
        }
        this.styling = new ChartStylingModel(Object.assign({}, this.activeTemplate.style, this.styling));
        // console.log('area-chart', this);
      });
    });
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'cf-area-chart',
  template: "<div *ngIf=\"_inited && properties?.display\" [class]=\"styling?.container?.class\" [ngClass]=\"getDynamicClasses(styling?.container?.dynamicClass)\"> <div #cfChartHost [style.height]=\"updateChartHeight(cfNgxChart?.chartElement?.nativeElement?.scrollHeight)\"> <cf-chart [style.width]=\"styling?.barsContainerWidth\" [style.height]=\"styling?.barsContainerHeight\" class=\"{{properties.legendPosition}} {{properties.chartAlignment}}\"> <ng-template #cfComponentChartTemplate> <ngx-charts-area-chart #cfNgxChart *ngIf=\"!properties?.stacked\" class=\"cf-chart-zone\"  [view]=\"properties?.view\" [scheme]=\"properties?.colorScheme\" [results]=\"properties?.data\" [gradient]=\"properties?.gradient\" [xAxis]=\"properties?.showXAxis\" [yAxis]=\"properties?.showYAxis\" [legend]=\"properties?.showLegend\" [showXAxisLabel]=\"properties?.showXAxisLabel\" [showYAxisLabel]=\"properties?.showYAxisLabel\" [xAxisLabel]=\"properties?.xAxisLabel\" [yAxisLabel]=\"properties?.yAxisLabel\" [autoScale]=\"properties?.autoScale\" (select)=\"onSelect($event)\"> </ngx-charts-area-chart> <ngx-charts-area-chart-stacked #cfNgxChart *ngIf=\"properties?.stacked\" class=\"cf-chart-zone\" [view]=\"properties?.view\" [scheme]=\"properties?.colorScheme\" [results]=\"properties?.data\" [gradient]=\"properties?.gradient\" [xAxis]=\"properties?.showXAxis\" [yAxis]=\"properties?.showYAxis\" [legend]=\"properties?.showLegend\" [showXAxisLabel]=\"properties?.showXAxisLabel\" [showYAxisLabel]=\"properties?.showYAxisLabel\" [xAxisLabel]=\"properties?.xAxisLabel\" [yAxisLabel]=\"properties?.yAxisLabel\" (select)=\"onSelect($event)\"> </ngx-charts-area-chart-stacked> </ng-template> </cf-chart> </div> </div> ",
  styles: [""]
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: TemplateService, },
{type: CfEventService, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'cfChartHost': [{ type: ViewChild, args: ['cfChartHost', ] },],
'cfNgxChart': [{ type: ViewChild, args: ['cfNgxChart', ] },],
'stacked': [{ type: Input, args: ['stacked', ] },],
'autoScale': [{ type: Input, args: ['autoScale', ] },],
};
}

function CfAreaChartComponent_tsickle_Closure_declarations() {
/** @type {?} */
CfAreaChartComponent.decorators;
/**
 * @nocollapse
 * @type {?}
 */
CfAreaChartComponent.ctorParameters;
/** @type {?} */
CfAreaChartComponent.propDecorators;
/**
 * @hidden
 * @type {?}
 */
CfAreaChartComponent.prototype.cfChartHost;
/**
 * @hidden
 * @type {?}
 */
CfAreaChartComponent.prototype.cfNgxChart;
/** @type {?} */
CfAreaChartComponent.prototype.elementRef;
/**
 * @hidden
 * @type {?}
 */
CfAreaChartComponent.prototype.eventService;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
